/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import apiRequest from "utils/apiRequestWithErrorCode";
import PageLoading from "components/PageLoading";
import MailDetails from "./MailDetails";
import RecipientsList from "./RecipientsList";
import { useEmailLogContext } from "../EmailLogProvider";
import { getEmailLogById } from "../api";

import { RecipientsByEmailResponse } from "@dashboard-v3/api";

type Status = "IDLE" | "LOADING_EMAIL" | "LOADING_RECIPIENTS";

export default function LogDetails() {
  const { t } = useTranslation("emailFlowLogs");
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { state, updateState } = useEmailLogContext();
  const [status, setStatus] = useState<Status>("IDLE");
  const [recipients, setRecipients] = useState<RecipientsByEmailResponse>([]);

  useEffect(() => {
    async function getSelectedEmail() {
      setStatus("LOADING_EMAIL");

      try {
        const selectedEmail = await getEmailLogById(id);
        updateState({ selectedEmail });
      } catch (e) {
        console.error("Failed to fetch email:", e);
        updateState({ selectedEmail: null });
      } finally {
        setStatus("IDLE");
      }
    }

    if (!state.selectedEmail && id) {
      getSelectedEmail();
    }
  }, []);

  useEffect(() => {
    async function fetchRecipientDetails() {
      setStatus("LOADING_RECIPIENTS");
      try {
        const { msgUID, recipients } = state.selectedEmail;
        const uri = `/emailflow-logs/recipients/${msgUID}`;
        const res = await apiRequest<RecipientsByEmailResponse>("POST", uri, {
          recipients,
        });
        setRecipients(res);
      } catch (error: unknown) {
        enqueueSnackbar(t("search.errors.fetchSearchResults"), {
          variant: "error",
        });
      } finally {
        setStatus("IDLE");
      }
    }

    if (state.selectedEmail && !recipients.length) fetchRecipientDetails();
  }, [state.selectedEmail]);

  function returnToList() {
    const { search } = location;
    const params = new URLSearchParams(search);
    history.push(`/emailflow-logs?${params}`);
  }

  if (status === "LOADING_EMAIL") return <PageLoading loading={true} />;

  if (!state.selectedEmail) {
    return (
      <Dialog open={true}>
        <DialogTitle>{t("emailDetails.warningModal.title")}</DialogTitle>
        <DialogContent>
          <Typography variant="caption">
            {t("emailDetails.warningModal.description")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={returnToList}
          >
            {t("emailDetails.warningModal.btn")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <>
      <MailDetails email={state.selectedEmail} />
      <RecipientsList
        loading={status === "LOADING_RECIPIENTS"}
        recipients={recipients}
      />
    </>
  );
}
